import React, { useCallback, useEffect, useRef, useState } from 'react';
import { fetchLastPunch, identifyAttendanceEmployee, punchAttendance } from '../api/attendance';
import { getErrorMessage } from '../api/client';
import { getCurrentPositionFresh } from '../utils/geolocation';
import WebcamCapture from './WebcamCapture';

function getEmployeeId(employee) {
  return employee?._id || employee?.id || '';
}

export default function AttendanceKiosk({
  initialPin = '',
  initialEmployee = null,
  showTitle = true,
  shellClassName = 'bg-white rounded-lg shadow p-6 space-y-4',
  onRecorded
}) {
  const [employeePin, setEmployeePin] = useState(initialPin);
  const [verifiedEmployee, setVerifiedEmployee] = useState(initialEmployee);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState(getEmployeeId(initialEmployee));
  const [punchType, setPunchType] = useState('in');
  const [facePhotoBase64, setFacePhotoBase64] = useState('');
  const [workNote, setWorkNote] = useState('');
  const [reportPhotoBase64, setReportPhotoBase64] = useState('');
  const [reportPhotoName, setReportPhotoName] = useState('');
  const [captureLocation, setCaptureLocation] = useState(null);
  const [lastPunchStatus, setLastPunchStatus] = useState(null);
  const [nowTick, setNowTick] = useState(Date.now());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [punchSummary, setPunchSummary] = useState(null);
  const [lastKnownLocation, setLastKnownLocation] = useState(null);
  const [locationMode, setLocationMode] = useState('pending');
  const [locationHint, setLocationHint] = useState('');
  const [alignNotice, setAlignNotice] = useState(false);
  const locationModeRef = useRef('pending');

  const lockedEmployee = Boolean(initialEmployee);

  useEffect(() => {
    setEmployeePin(initialPin || '');
    if (initialEmployee) {
      setVerifiedEmployee(initialEmployee);
      setSelectedEmployeeId(getEmployeeId(initialEmployee));
    }
  }, [initialPin, initialEmployee]);

  useEffect(() => {
    locationModeRef.current = locationMode;
  }, [locationMode]);

  useEffect(() => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      setLocationMode('unsupported');
      setLastKnownLocation(null);
      return undefined;
    }
    setLocationMode('pending');
    setLastKnownLocation(null);
    setLocationHint('');
    setAlignNotice(false);

    const alignTimer = setTimeout(() => setAlignNotice(true), 8000);
    const bootstrapTimer = setTimeout(() => {
      if (locationModeRef.current !== 'pending') return;
      navigator.geolocation.getCurrentPosition(
        (p) => {
          setLastKnownLocation({
            lat: p.coords.latitude,
            lng: p.coords.longitude,
            accuracy: p.coords.accuracy,
            capturedAt: new Date().toISOString()
          });
          setLocationMode('ready');
          setLocationHint('');
          setAlignNotice(false);
        },
        () => {},
        { enableHighAccuracy: false, maximumAge: 0, timeout: 18000 }
      );
    }, 9000);

    const watchId = navigator.geolocation.watchPosition(
      (p) => {
        clearTimeout(alignTimer);
        setAlignNotice(false);
        setLastKnownLocation({
          lat: p.coords.latitude,
          lng: p.coords.longitude,
          accuracy: p.coords.accuracy,
          capturedAt: new Date().toISOString()
        });
        setLocationMode('ready');
        setLocationHint('');
      },
      (err) => {
        clearTimeout(alignTimer);
        setLastKnownLocation(null);
        if (err.code === 1) {
          setLocationMode('denied');
          setLocationHint('Location permission is off. Allow location for this site in your browser settings.');
        } else {
          setLocationMode('error');
          setLocationHint(
            err.code === 3
              ? 'GPS is taking too long. Move outdoors or near a window, then tap "Retry location".'
              : 'Could not detect location. Go to an open area with a clear view of the sky and retry.'
          );
        }
      },
      { enableHighAccuracy: true, maximumAge: 10000, timeout: 22000 }
    );

    return () => {
      clearTimeout(alignTimer);
      clearTimeout(bootstrapTimer);
      navigator.geolocation.clearWatch(watchId);
    };
  }, []);

  const retryLocation = useCallback(async () => {
    setError('');
    setLocationHint('');
    setAlignNotice(false);
    if (!navigator.geolocation) {
      setLocationMode('unsupported');
      return;
    }
    setLocationMode('pending');
    setLastKnownLocation(null);
    try {
      const fix = await getCurrentPositionFresh({
        highAccuracyTimeout: 14000,
        lowAccuracyTimeout: 22000
      });
      setLastKnownLocation({
        lat: fix.lat,
        lng: fix.lng,
        accuracy: fix.accuracy,
        capturedAt: fix.capturedAt
      });
      setLocationMode('ready');
    } catch (err) {
      setLastKnownLocation(null);
      const msg = err?.message || '';
      if (msg.includes('denied') || msg.includes('permission')) {
        setLocationMode('denied');
        setLocationHint('Allow location access when prompted, or enable it in site settings.');
      } else {
        setLocationMode('error');
        setLocationHint(
          'Still no location fix. On laptops, Wi-Fi location may need a moment; stand by a window or try again.'
        );
      }
    }
  }, []);

  const getDeviceInfo = () => {
    const ua = navigator.userAgent || '';
    let browser = 'Unknown';
    if (ua.includes('Chrome')) browser = 'Chrome';
    else if (ua.includes('Firefox')) browser = 'Firefox';
    else if (ua.includes('Safari')) browser = 'Safari';
    else if (ua.includes('Edge')) browser = 'Edge';
    let os = 'Unknown';
    if (ua.includes('Windows')) os = 'Win';
    else if (ua.includes('Mac')) os = 'Mac';
    else if (ua.includes('Android')) os = 'Android';
    else if (ua.includes('iPhone') || ua.includes('iPad')) os = 'iOS';
    return `${browser}/${os}`;
  };

  const addOverlayToPhoto = useCallback(async (blob, fix) => {
    return new Promise((resolve) => {
      const img = new Image();
      const url = URL.createObjectURL(blob);
      img.onload = () => {
        URL.revokeObjectURL(url);
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0);
        const ts = new Date(fix.capturedAt).toLocaleString(undefined, {
          dateStyle: 'short',
          timeStyle: 'medium'
        });
        const loc = `${Number(fix.lat).toFixed(5)}, ${Number(fix.lng).toFixed(5)}`;
        const acc =
          fix.accuracy != null && Number.isFinite(fix.accuracy)
            ? ` +/-${Math.round(fix.accuracy)}m`
            : '';
        const device = getDeviceInfo();
        const line = `${ts} | ${loc}${acc} | ${device}`;
        const pad = 8;
        const fontH = 16;
        const barH = fontH + pad * 2;
        ctx.fillStyle = 'rgba(0,0,0,0.6)';
        ctx.fillRect(0, canvas.height - barH, canvas.width, barH);
        ctx.fillStyle = '#fff';
        ctx.font = `${Math.min(14, canvas.width / 30)}px Arial`;
        ctx.textBaseline = 'middle';
        ctx.fillText(line, pad, canvas.height - barH / 2);
        canvas.toBlob((b) => resolve(b || blob), 'image/jpeg', 0.9);
      };
      img.onerror = () => {
        URL.revokeObjectURL(url);
        resolve(blob);
      };
      img.src = url;
    });
  }, []);

  useEffect(() => {
    const id = setInterval(() => setNowTick(Date.now()), 30000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    if (!selectedEmployeeId) {
      setPunchType('in');
      setLastPunchStatus(null);
      return;
    }
    fetchLastPunch(selectedEmployeeId)
      .then((status) => {
        setLastPunchStatus(status || null);
        setPunchType(status?.type === 'in' ? 'out' : 'in');
      })
      .catch(() => setPunchType('in'));
  }, [selectedEmployeeId]);

  useEffect(() => {
    setFacePhotoBase64('');
    setWorkNote('');
    setReportPhotoBase64('');
    setReportPhotoName('');
    setCaptureLocation(null);
  }, [selectedEmployeeId]);

  const locationOkForCapture = locationMode === 'ready' && lastKnownLocation != null;
  const locationBlocked =
    locationMode === 'unsupported' || locationMode === 'denied' || locationMode === 'error';
  const earliestClockOutAt = lastPunchStatus?.earliestClockOutAt
    ? new Date(lastPunchStatus.earliestClockOutAt)
    : null;
  const earlyClockOutBlocked =
    punchType === 'out' &&
    earliestClockOutAt &&
    !Number.isNaN(earliestClockOutAt.getTime()) &&
    earliestClockOutAt.getTime() > nowTick;
  const earlyClockOutRemainingMinutes = earlyClockOutBlocked
    ? Math.max(1, Math.ceil((earliestClockOutAt.getTime() - nowTick) / 60000))
    : 0;
  const earlyClockOutTimeLabel = earliestClockOutAt && !Number.isNaN(earliestClockOutAt.getTime())
    ? earliestClockOutAt.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
    : '';
  const formatSummaryTime = useCallback((value) => {
    const date = value ? new Date(value) : new Date();
    return date.toLocaleString('en-PH', {
      timeZone: 'Asia/Manila',
      month: 'short',
      day: '2-digit',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    });
  }, []);

  const handleCapture = useCallback(
    async (blob) => {
      setError('');
      let fix;
      try {
        fix = await getCurrentPositionFresh({
          highAccuracyTimeout: 14000,
          lowAccuracyTimeout: 22000
        });
      } catch (err) {
        setCaptureLocation(null);
        setFacePhotoBase64('');
        setError(
          getErrorMessage(
            err,
            'Location not detected at capture time. Move to an open area, align for GPS, allow location access, then capture again.'
          )
        );
        return;
      }

      try {
        const overlayBlob = await addOverlayToPhoto(blob, fix);
        if (!overlayBlob) return;
        setCaptureLocation({
          lat: fix.lat,
          lng: fix.lng,
          capturedAt: fix.capturedAt
        });
        const reader = new FileReader();
        reader.onloadend = () => setFacePhotoBase64(reader.result || '');
        reader.readAsDataURL(overlayBlob);
      } catch (_) {
        setCaptureLocation(null);
        setFacePhotoBase64('');
        setError('Could not add timestamp to photo. Try capturing again.');
      }
    },
    [addOverlayToPhoto]
  );

  const handlePunch = useCallback(async () => {
    if (!selectedEmployeeId) {
      setError('Enter your employee PIN first.');
      return;
    }
    if (!facePhotoBase64) {
      setError('Capture your face photo before clocking in/out.');
      return;
    }
    if (
      !captureLocation ||
      typeof captureLocation.lat !== 'number' ||
      typeof captureLocation.lng !== 'number' ||
      !captureLocation.capturedAt
    ) {
      setError('Location with timestamp is missing. Capture your photo again with GPS enabled.');
      return;
    }
    if (punchType === 'out' && !workNote.trim()) {
      setError('Enter your work note before clocking out.');
      return;
    }
    if (earlyClockOutBlocked) {
      setError(`Clock out is too early. Please wait ${earlyClockOutRemainingMinutes} more minute${earlyClockOutRemainingMinutes === 1 ? '' : 's'} until ${earlyClockOutTimeLabel}.`);
      return;
    }
    setError('');
    setSuccess('');
    setLoading(true);
    try {
      const currentPunchType = punchType;
      const result = await punchAttendance({
        employeeId: selectedEmployeeId,
        pin: employeePin,
        type: currentPunchType,
        facePhotoBase64,
        workNote: currentPunchType === 'out' ? workNote.trim() : undefined,
        reportPhotoBase64: currentPunchType === 'out' ? reportPhotoBase64 || undefined : undefined,
        location: {
          lat: captureLocation.lat,
          lng: captureLocation.lng,
          capturedAt: captureLocation.capturedAt
        }
      });
      const employeeName =
        result?.employeeId?.name ||
        result?.employee?.name ||
        verifiedEmployee?.name ||
        'Employee';
      const recordedAt = result?.timestamp || new Date().toISOString();
      setPunchSummary({
        employeeName,
        action: currentPunchType === 'in' ? 'Clock In' : 'Clock Out',
        timestamp: recordedAt,
        timeLabel: `${formatSummaryTime(recordedAt)} PHT`
      });
      setSuccess(`${currentPunchType === 'in' ? 'Clock in' : 'Clock out'} recorded.`);
      setFacePhotoBase64('');
      setWorkNote('');
      setReportPhotoBase64('');
      setReportPhotoName('');
      setCaptureLocation(null);
      onRecorded?.(result);
      fetchLastPunch(selectedEmployeeId)
        .then((status) => {
          setLastPunchStatus(status || null);
          setPunchType(status?.type === 'in' ? 'out' : 'in');
        })
        .catch(() => {
          setLastPunchStatus(null);
          setPunchType('in');
        });
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to record attendance.'));
    } finally {
      setLoading(false);
    }
  }, [selectedEmployeeId, employeePin, punchType, facePhotoBase64, workNote, reportPhotoBase64, captureLocation, earlyClockOutBlocked, earlyClockOutRemainingMinutes, earlyClockOutTimeLabel, verifiedEmployee?.name, formatSummaryTime, onRecorded]);

  const handleReportPhotoChange = useCallback((event) => {
    const file = event.target.files?.[0];
    setReportPhotoBase64('');
    setReportPhotoName('');
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setError('Attach an image file for the work report photo.');
      event.target.value = '';
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setError('Report photo must be 5 MB or smaller.');
      event.target.value = '';
      return;
    }
    const reader = new FileReader();
    reader.onloadend = () => {
      setReportPhotoBase64(reader.result || '');
      setReportPhotoName(file.name);
    };
    reader.onerror = () => setError('Could not attach the report photo. Try another image.');
    reader.readAsDataURL(file);
  }, []);

  const handleVerifyPin = useCallback(async (event) => {
    event.preventDefault();
    const cleanPin = employeePin.trim();
    if (!cleanPin) {
      setError('Enter your employee PIN.');
      return;
    }
    setError('');
    setSuccess('');
    setLoading(true);
    setFacePhotoBase64('');
    setCaptureLocation(null);
    try {
      const emp = await identifyAttendanceEmployee({ pin: cleanPin });
      const id = getEmployeeId(emp);
      if (!id) throw new Error('Employee was not returned by the server.');
      setVerifiedEmployee(emp);
      setSelectedEmployeeId(id);
      const status = await fetchLastPunch(id).catch(() => null);
      setLastPunchStatus(status || null);
      setPunchType(status?.type === 'in' ? 'out' : 'in');
    } catch (err) {
      setVerifiedEmployee(null);
      setSelectedEmployeeId('');
      setError(getErrorMessage(err, 'Invalid PIN. Please try again.'));
    } finally {
      setLoading(false);
    }
  }, [employeePin]);

  const formatLocShort = (loc) =>
    loc ? `${Number(loc.lat).toFixed(4)}, ${Number(loc.lng).toFixed(4)}` : '';

  return (
    <div className={shellClassName}>
      {error && <p className="text-red-600">{error}</p>}
      {success && <p className="text-green-600">{success}</p>}

      {punchSummary && (
        <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/60 px-4" role="dialog" aria-modal="true" aria-labelledby="attendance-summary-title">
          <div className="w-full max-w-sm overflow-hidden rounded-2xl bg-white shadow-2xl">
            <div className={`px-5 py-4 text-white ${punchSummary.action === 'Clock Out' ? 'bg-amber-600' : 'bg-indigo-600'}`}>
              <p className="text-sm font-medium opacity-90">Attendance recorded</p>
              <h2 id="attendance-summary-title" className="mt-1 text-2xl font-bold">{punchSummary.action}</h2>
            </div>
            <div className="space-y-4 p-5">
              <div>
                <p className="text-xs uppercase tracking-wide text-gray-500">Employee</p>
                <p className="text-lg font-semibold text-gray-900">{punchSummary.employeeName}</p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-wide text-gray-500">Time</p>
                <p className="text-lg font-semibold text-gray-900">{punchSummary.timeLabel}</p>
              </div>
              <div className="rounded-lg border bg-gray-50 p-3 text-sm text-gray-700">
                {punchSummary.employeeName} - {punchSummary.timeLabel}
              </div>
              <button
                type="button"
                onClick={() => setPunchSummary(null)}
                className="w-full rounded-lg bg-gray-900 py-3 font-medium text-white hover:bg-gray-800"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      {showTitle ? <h2 className="text-lg font-semibold">Punch In / Out</h2> : null}

      {locationMode === 'unsupported' && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-900" role="alert">
          <p className="font-medium">Location not available</p>
          <p className="mt-1">This device does not support GPS. Attendance capture requires location.</p>
        </div>
      )}

      {(locationMode === 'denied' || locationMode === 'error') && (
        <div className="space-y-2 rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm text-amber-950" role="alert">
          <p className="font-medium">Align location to continue</p>
          <p>
            Turn on <strong>Location</strong> for this site, stand <strong>near a window or outdoors</strong>,
            and wait until GPS locks. Photo capture stays off until your position is detected.
          </p>
          {locationHint ? <p className="text-amber-900">{locationHint}</p> : null}
          <button
            type="button"
            onClick={retryLocation}
            className="min-touch mt-2 rounded-md bg-amber-700 px-4 py-2 text-sm font-medium text-white hover:bg-amber-800"
          >
            Retry location
          </button>
        </div>
      )}

      {locationMode === 'pending' && (
        <div className="space-y-2 rounded-lg border border-blue-200 bg-blue-50 p-4 text-sm text-blue-950" role="status">
          <p className="font-medium">Detecting your location...</p>
          {alignNotice ? (
            <p>
              <strong>Align for GPS:</strong> move outdoors or beside a window, hold the device steady,
              and ensure location permission is allowed. Capture stays disabled until a fix is found.
            </p>
          ) : (
            <p>Please wait while we lock your position.</p>
          )}
          <button
            type="button"
            onClick={retryLocation}
            className="mt-1 text-sm font-medium text-blue-800 underline"
          >
            Retry location now
          </button>
        </div>
      )}

      {locationMode === 'ready' && lastKnownLocation && (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-green-200 bg-green-50 p-3 text-sm text-green-900">
          <span>
            Location OK - {formatLocShort(lastKnownLocation)}
            {lastKnownLocation.accuracy != null
              ? ` - accuracy ~${Math.round(lastKnownLocation.accuracy)} m`
              : ''}
          </span>
          <button
            type="button"
            onClick={retryLocation}
            className="shrink-0 text-sm text-green-800 underline"
          >
            Refresh
          </button>
        </div>
      )}

      {!lockedEmployee && (
        <form onSubmit={handleVerifyPin} className="space-y-2">
          <label className="block text-sm text-gray-600" htmlFor="employeePin">Employee PIN</label>
          <div className="flex flex-col gap-2 sm:flex-row">
            <input
              id="employeePin"
              type="password"
              inputMode="numeric"
              autoComplete="off"
              value={employeePin}
              onChange={(e) => {
                setEmployeePin(e.target.value);
                setVerifiedEmployee(null);
                setSelectedEmployeeId('');
                setFacePhotoBase64('');
                setWorkNote('');
                setReportPhotoBase64('');
                setReportPhotoName('');
                setCaptureLocation(null);
              }}
              className="w-full rounded border px-3 py-2"
              aria-label="Employee PIN"
              placeholder="Enter PIN"
            />
            <button
              type="submit"
              disabled={loading || !employeePin.trim()}
              className="rounded bg-gray-900 px-4 py-2 font-medium text-white disabled:opacity-50"
            >
              Continue
            </button>
          </div>
        </form>
      )}

      {verifiedEmployee && (
        <div className="rounded-lg border border-indigo-200 bg-indigo-50 p-3 text-sm text-indigo-950">
          <p className="font-medium">{verifiedEmployee.name}</p>
          {verifiedEmployee.role ? <p className="text-indigo-800">{verifiedEmployee.role}</p> : null}
        </div>
      )}

      <div>
        <p className="mb-1 text-sm text-gray-600">
          {punchType === 'in' ? 'Next: Clock In' : 'Next: Clock Out'}
        </p>
        <p className="text-xs text-gray-500">
          {punchType === 'in' ? 'You have not clocked in yet, or your last punch was Clock Out.' : 'You are clocked in. Tap below to Clock Out.'}
        </p>
        {earlyClockOutBlocked && (
          <div className="mt-3 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-950">
            <p className="font-medium">Clock out locked until {earlyClockOutTimeLabel}</p>
            <p className="mt-1">
              Please wait {earlyClockOutRemainingMinutes} more minute{earlyClockOutRemainingMinutes === 1 ? '' : 's'} before clocking out.
            </p>
          </div>
        )}
      </div>

      {punchType === 'out' && (
        <div className="space-y-3 rounded-lg border border-amber-200 bg-amber-50 p-4">
          <div>
            <label className="mb-1 block text-sm font-medium text-amber-950" htmlFor="workNote">
              Work note before clock out
            </label>
            <textarea
              id="workNote"
              value={workNote}
              onChange={(e) => setWorkNote(e.target.value)}
              rows={4}
              maxLength={1000}
              className="w-full rounded border border-amber-200 bg-white px-3 py-2 text-sm"
              placeholder="Summarize the jobs done today"
            />
            <p className="mt-1 text-xs text-amber-800">{workNote.trim().length}/1000</p>
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-amber-950">
              Report photo (optional)
            </label>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              <label className="flex cursor-pointer items-center justify-center rounded-md bg-amber-700 px-3 py-2 text-sm font-medium text-white hover:bg-amber-800">
                Take photo
                <input
                  type="file"
                  accept="image/*"
                  capture="environment"
                  onChange={handleReportPhotoChange}
                  className="sr-only"
                  aria-label="Take report photo"
                />
              </label>
              <label className="flex cursor-pointer items-center justify-center rounded-md border border-amber-300 bg-white px-3 py-2 text-sm font-medium text-amber-950 hover:bg-amber-100">
                Choose from gallery
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleReportPhotoChange}
                  className="sr-only"
                  aria-label="Choose report photo from gallery"
                />
              </label>
            </div>
            <p className="mt-1 text-xs text-amber-800">Use camera for a new report photo, or attach an existing image from your phone gallery.</p>
            {reportPhotoName ? (
              <p className="mt-1 text-xs text-green-700">Attached: {reportPhotoName}</p>
            ) : null}
          </div>
        </div>
      )}

      <div>
        <label className="mb-1 block text-sm text-gray-600">Face photo (required)</label>
        {!locationOkForCapture && (
          <p className="mb-2 rounded border border-amber-200 bg-amber-50 p-2 text-sm text-amber-800">
            Capture is disabled until your location is detected. See the notice above.
          </p>
        )}
        <WebcamCapture
          onCapture={handleCapture}
          disabled={!verifiedEmployee || !locationOkForCapture || locationBlocked}
          className="max-w-xs"
        />
        {facePhotoBase64 && captureLocation && (
          <p className="mt-1 text-xs text-green-600">
            Photo captured with GPS timestamp. Ready to clock {punchType === 'in' ? 'in' : 'out'}.
          </p>
        )}
      </div>

      <button
        type="button"
        onClick={handlePunch}
        disabled={
          loading ||
          !verifiedEmployee ||
          !facePhotoBase64 ||
          !captureLocation ||
          earlyClockOutBlocked ||
          (punchType === 'out' && !workNote.trim())
        }
        className={`w-full rounded py-3 font-medium disabled:opacity-50 ${punchType === 'out' ? 'bg-amber-600 text-white hover:bg-amber-700' : 'bg-indigo-600 text-white'}`}
      >
        {loading ? 'Recording...' : punchType === 'in' ? 'Clock In' : 'Clock Out'}
      </button>
    </div>
  );
}
